import { NOT_ASSIGNED } from 'constants/constants';
import React from 'react';
import { VerificationType } from 'services/EpdClient';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { EPDBasedOnUnitEnum, VerifierType } from 'types/types';
import getSubtypeLabel from 'util/getSubtypeLabel';
import getVerificationTypeLabel from 'util/getVerificationTypeLabel';
import { formatCompanyAddress, formatDate } from 'util/utils';

import { EPDProductClassificationId } from '../../constants';
import VersionHistorySummaryGrid, { SummaryGridRow } from '../VersionHistorySummaryGrid';
import { EpdVersionHistoryData, useEpdVersionHistoryData } from '../utils/EpdVersionHistoryDataContext';
import usePcrV2 from '../../hooks/usePcrV2';
import { getDisplayRegionName } from 'pages/v2/compiler/PDF/utils/getDisplayRegionName';

type LabelsAccordingToPCRModel = {
  sitesSelectionDescription: string;
  documentCoverageStatement: string;
  epdClassificationDictionaryCode: string;
};

const VersionHistoryGeneralInformationGrid: React.FC<{}> = () => {
  const epdVersionHistoryData = useEpdVersionHistoryData();
  const isPcrV2 = usePcrV2();

  const rows = epdVersionHistoryData ? getVersionGeneralInformationRows(epdVersionHistoryData, isPcrV2) || [] : [];
  return <VersionHistorySummaryGrid rows={rows} />;
};

export default VersionHistoryGeneralInformationGrid;

export const getVersionGeneralInformationRows = (data: EpdVersionHistoryData, isPcrV2: boolean): SummaryGridRow[] => {
  const {
    epdDefinitionInformation,
    organizationInformation,
    organizationInformationAddress,
    pcrInformation,
    lcaSupportInformation,
    referenceFlow,
    conversionFactor,
    thirdPartyVerification,
    verificationReportInfo,
    additionalInformation,
    lcaSpecification,
    countries,
    regions,
    dictionaries,
  } = data || {};

  const labelsAccordingToPCR: LabelsAccordingToPCRModel = isPcrV2
    ? {
        sitesSelectionDescription:
          'How the selection of the sites/products has been done and the average/Worst-case has been determined',
        documentCoverageStatement: 'Statement about average/worst-case values',
        epdClassificationDictionaryCode: EPD_DICTIONARY_CODES.EPD_CLASSIFICATION_V2,
      }
    : {
        sitesSelectionDescription:
          'How the selection of the sites/products has been done and the average has been determined',
        documentCoverageStatement:
          'A statement that the EPD covers average values for an entire or partial product category specifying the percentage of representativeness and, hence, the declared product is an average that is not available for purchase on the market and that the results are not representative for any specific manufacturer or its product',
        epdClassificationDictionaryCode: EPD_DICTIONARY_CODES.EPD_CLASSIFICATION,
      };

  const possibleRows: (SummaryGridRow | false)[] = [
    {
      ord: 1,
      name: 'EPD name',
      value: epdDefinitionInformation.declarationName,
    },
    {
      ord: 2,
      name: 'UUID of data set',
      value: epdDefinitionInformation.id,
    },
    {
      ord: 3,
      name: 'EPD ID',
      value: epdDefinitionInformation.fullIdentificationNumber,
    },
    {
      ord: 4,
      name: 'EPD version',
      value: String(epdDefinitionInformation.versionNumber).padStart(3, '0'),
    },
    {
      ord: 5,
      name: 'Version history',
      value: epdDefinitionInformation.versionDescription ?? NOT_ASSIGNED,
      isHtmlRichText: true,
    },
    {
      ord: 6,
      name: 'Single or multiple products',
      value: (() => {
        if (!epdDefinitionInformation.epdClassification) {
          return NOT_ASSIGNED;
        }

        return (
          dictionaries.find(
            (d) =>
              d.dictionaryCode === labelsAccordingToPCR.epdClassificationDictionaryCode &&
              d.key === epdDefinitionInformation.epdClassification
          )?.value ?? NOT_ASSIGNED
        );
      })(),
    },
    (epdDefinitionInformation.epdClassification ===
      EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_REPRESENTATIVE_PRODUCT_ID ||
      epdDefinitionInformation.epdClassification ===
        EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_SEVERAL_REPRESENTATIVE_PRODUCTS_ID) && {
      ord: 7,
      name: 'Justification for why this is representative',
      value: epdDefinitionInformation.representativeJustification ?? NOT_ASSIGNED,
    },
    epdDefinitionInformation.epdClassification === EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_AVG_RESULTS_ID && {
      ord: 8,
      name: 'Description of how the averages have been determined',
      value: epdDefinitionInformation.determinationOfAverageDescription ?? NOT_ASSIGNED,
    },
    ...(epdDefinitionInformation.epdClassification === EPDProductClassificationId.SECTOR_OF_MULTIPLE_PRODUCTS_ID ||
    epdDefinitionInformation.epdClassification === EPDProductClassificationId.SECTOR_OF_AVERAGE_PRODUCTS_ID ||
    epdDefinitionInformation.epdClassification === EPDProductClassificationId.SECTOR_OF_WORST_CASE_PRODUCTS_ID
      ? [
          {
            ord: 9,
            name: 'A list of the contributing manufacturers that the Sector EPD covers',
            value: epdDefinitionInformation.listOfContributingManufacturers ?? NOT_ASSIGNED,
          },
          {
            ord: 9,
            name: labelsAccordingToPCR.sitesSelectionDescription,
            value: epdDefinitionInformation.sitesSelectionDescription ?? NOT_ASSIGNED,
          },
          {
            ord: 9,
            name: labelsAccordingToPCR.documentCoverageStatement,
            value: epdDefinitionInformation.documentCoverageStatement ?? NOT_ASSIGNED,
          },
        ]
      : []),
    {
      ord: 10,
      name: 'Product to declare',
      value: epdDefinitionInformation.productType || NOT_ASSIGNED,
    },
    {
      ord: 11,
      name: 'Standards conformance',
      value: (() => {
        if (!epdDefinitionInformation.statementOfConformityWithStandards) {
          return NOT_ASSIGNED;
        }

        const standardsValues = Array.isArray(epdDefinitionInformation.statementOfConformityWithStandards)
          ? epdDefinitionInformation.statementOfConformityWithStandards
          : epdDefinitionInformation.statementOfConformityWithStandards.split(',').map((x) => Number(x));
        if (standardsValues.length === 0) {
          return NOT_ASSIGNED;
        }

        const standards = standardsValues.map(
          (s) =>
            dictionaries.find((d) => d.dictionaryCode === EPD_DICTIONARY_CODES.CONFORMITY_STANDARDS && d.key === s)?.value ??
            NOT_ASSIGNED
        );
        return `In accordance with ${standards.join(', ')}`;
      })(),
    },
    {
      ord: 12,
      name: 'Keywords',
      value: epdDefinitionInformation.tags?.map((t) => t.label)?.join(', ') || NOT_ASSIGNED,
    },
    {
      ord: 13,
      name: 'Use advice for the EPD',
      value: epdDefinitionInformation.useAdvice ?? NOT_ASSIGNED,
    },
    {
      ord: 14,
      name: 'Subtype for ILCD+EPD',
      value: (epdDefinitionInformation.subtype && getSubtypeLabel(epdDefinitionInformation.subtype)) ?? NOT_ASSIGNED,
    },
    {
      ord: 15,
      name: 'EPD based on declared or functional unit',
      value: (() => {
        if (!lcaSpecification || !lcaSpecification.epdBasedOnUnit) {
          return NOT_ASSIGNED;
        }
        const index = Object.keys(EPDBasedOnUnitEnum).indexOf(lcaSpecification.epdBasedOnUnit);
        if (index === -1) {
          return 'Unsupported value';
        }
        const dictionaryEntry = dictionaries.find(
          (d) => d.dictionaryCode === EPD_DICTIONARY_CODES.EPD_BASED_ON_UNIT && d.key === index
        );
        if (!dictionaryEntry) {
          return NOT_ASSIGNED;
        }
        return dictionaryEntry.value;
      })(),
    },
    !!lcaSpecification &&
      lcaSpecification.epdBasedOnUnit === EPDBasedOnUnitEnum.FunctionalUnit && {
        ord: 16,
        name: 'Functional unit description',
        value: lcaSpecification.functionalUnitDescription ?? NOT_ASSIGNED,
      },
    !!lcaSpecification && {
      ord: 17,
      name:
        lcaSpecification.epdBasedOnUnit === EPDBasedOnUnitEnum.DeclaredUnit
          ? 'Declared unit and reference flow'
          : 'Reference flow',
      value: (() => {
        if (!referenceFlow) {
          return NOT_ASSIGNED;
        }

        const lines = [];
        if (referenceFlow.productFlowName) {
          lines.push(referenceFlow.productFlowName);
        }
        const unitEntry =
          referenceFlow.unitId &&
          dictionaries.find(
            (d) =>
              d.dictionaryCode === EPD_DICTIONARY_CODES.REFERENCE_FLOW_UNIT &&
              d.value.toUpperCase() === referenceFlow.unitId.toUpperCase()
          );
        if (unitEntry) {
          lines.push(`${unitEntry.description}, ${referenceFlow.value}`);
        }

        return lines.join('\n');
      })(),
      isHtmlRichText: true,
    },
    {
      ord: 18,
      name: 'Conversion factor to mass',
      value: (() => {
        if (!conversionFactor) {
          return NOT_ASSIGNED;
        }

        return `${conversionFactor.value}`;
      })(),
    },
    {
      ord: 19,
      name: 'PCR',
      value: pcrInformation.pcr?.fullName ?? NOT_ASSIGNED,
    },
    !!pcrInformation.cpcr && {
      ord: 20,
      name: 'C-PCR',
      value: pcrInformation.cpcr.fullName ?? NOT_ASSIGNED,
    },
    !!pcrInformation.cpcr && {
      ord: 21,
      name: 'Choose product category',
      value: pcrInformation.cpcrProductCategory?.title ?? NOT_ASSIGNED,
    },
    {
      ord: 22,
      name: 'LCA Practitioner',
      value: lcaSupportInformation.epdDevelopers?.map((d) => d.contactName).join(', ') ?? NOT_ASSIGNED,
    },
    {
      ord: 23,
      name: 'Verification type',
      value: thirdPartyVerification.verificationType
        ? getVerificationTypeLabel(thirdPartyVerification.verificationType)
        : NOT_ASSIGNED,
    },
    thirdPartyVerification.isCreatedByApiFullyIntegratedPreverifiedTool && {
      ord: 24,
      name: 'Tool name',
      value: thirdPartyVerification.preverifiedTool?.name ?? NOT_ASSIGNED,
    },
    !thirdPartyVerification.isCreatedByApiFullyIntegratedPreverifiedTool &&
      thirdPartyVerification.verificationType === VerificationType.EPDPreverifiedTool && {
        ord: 24,
        name: 'Pre-verified tool',
        value: thirdPartyVerification.preverifiedTool?.name ?? NOT_ASSIGNED,
      },
    thirdPartyVerification.verificationType === VerificationType.EPDPreverifiedTool && {
      ord: 25,
      name: 'Tool version',
      value: thirdPartyVerification.preverifiedToolVersion?.name ?? NOT_ASSIGNED,
    },
    !thirdPartyVerification.isCreatedByApiFullyIntegratedPreverifiedTool && {
      ord: 26,
      name: 'EPD Verifier',
      value:
        (thirdPartyVerification.verificationType === VerificationType.EPDProcessCertification
          ? thirdPartyVerification.processCertificateIssuedBy?.homeCompanyName
          : thirdPartyVerification.epdVerifier?.displayName) ?? NOT_ASSIGNED,
    },
    !thirdPartyVerification.isCreatedByApiFullyIntegratedPreverifiedTool &&
      (thirdPartyVerification.verificationType === VerificationType.EPDVerification ||
        thirdPartyVerification.verificationType === VerificationType.EPDPreverifiedTool) &&
      !!thirdPartyVerification.epdVerifier && {
        ord: 27,
        name: thirdPartyVerification.epdVerifier.verifierType === VerifierType.Body ? 'Accredited by' : 'Approved by',
        value:
          thirdPartyVerification.epdVerifier.verifierType === VerifierType.Body
            ? thirdPartyVerification.epdVerifier.information ?? NOT_ASSIGNED
            : 'International EPD System',
      },
    thirdPartyVerification.verificationType === VerificationType.EPDVerification &&
      thirdPartyVerification.epdVerifier?.verifierType === VerifierType.Body && {
        ord: 28,
        name: 'Accredited certification body address',
        value: formatCompanyAddress(thirdPartyVerification.epdVerifier.companyAddress, NOT_ASSIGNED),
      },
    thirdPartyVerification.verificationType === VerificationType.EPDProcessCertification &&
      !!thirdPartyVerification.processCertificateIssuedBy && {
        ord: 29,
        name: 'Accredited by',
        value: thirdPartyVerification.processCertificateIssuedBy.information ?? NOT_ASSIGNED,
      },
    thirdPartyVerification.verificationType === VerificationType.EPDProcessCertification &&
      thirdPartyVerification.processCertificateIssuedBy?.verifierType === VerifierType.Body && {
        ord: 30,
        name: 'Accredited certification body address',
        value: formatCompanyAddress(thirdPartyVerification.processCertificateIssuedBy.companyAddress, NOT_ASSIGNED),
      },
    {
      ord: 31,
      name: 'Procedure for follow-up of data during EPD validity involves third party verifier',
      value: (() => {
        if (!thirdPartyVerification.followUpInvolvesThirdPartyVerifier) {
          return NOT_ASSIGNED;
        }
        return thirdPartyVerification.followUpInvolvesThirdPartyVerifier;
      })(),
    },
    {
      ord: 32,
      name: 'EPD Owner',
      value: organizationInformation.companyName,
    },
    {
      ord: 33,
      name: 'Contact person e-mail',
      value: organizationInformation.contactPersonEmail ?? NOT_ASSIGNED,
    },
    {
      ord: 34,
      name: 'Contact person name',
      value: organizationInformation.contactPersonName ?? NOT_ASSIGNED,
    },
    {
      ord: 35,
      name: 'Organisation address',
      value: (() => {
        const details = [
          organizationInformationAddress.address.addressLine1,
          organizationInformationAddress.address.addressLine2,
          [organizationInformationAddress.address.zipCode, organizationInformationAddress.address.city]
            .filter(Boolean)
            .join(' '),
          getDisplayRegionName(organizationInformationAddress.address.regionId, regions),
          countries.find((c) => c.id === organizationInformationAddress.address.countryId)?.name,
        ]
          .filter((x) => x)
          .join('\n');
        return details || NOT_ASSIGNED;
      })(),
    },
    {
      ord: 36,
      name: 'Description of the organisation',
      value: organizationInformation.description ?? NOT_ASSIGNED,
    },
    {
      ord: 37,
      name: 'Additional environmental information',
      value: additionalInformation?.environmental || '',
      isHtmlRichText: true,
    },
    !isPcrV2 && {
      ord: 38,
      name: 'Conversion factors',
      value: additionalInformation?.conversion || '',
      isHtmlRichText: true,
    },
    !isPcrV2 && {
      ord: 39,
      name: 'Dangerous substances to indoor air, soil, and water during the use stage',
      value: additionalInformation?.dangerous || '',
      isHtmlRichText: true,
    },
    {
      ord: 40,
      name: 'Additional social and economic information',
      value: additionalInformation?.economic || '',
      isHtmlRichText: true,
    },
    isPcrV2 && {
      ord: 41,
      name: 'Abbreviations',
      value: additionalInformation?.abbreviations || '',
      isHtmlRichText: true,
    },
  ];

  return possibleRows.filter(Boolean) as SummaryGridRow[];
};
